import { type ChangeEvent, type FocusEvent, type InvalidEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { InputText as UiCoreInputText } from '@ev-tech/ui-core-components/InputText';
import { datadogRum } from '@datadog/browser-rum';

import { useInputValidation } from '@/hooks/useInputValidation';

import { normalizeInputTextValueProps } from './TextInput.utils';
import { type ExposeInputTextProps } from './TextInput.types';
import { ModalContext } from '../Modal/ModalContext';

import styles from './TextInput.module.css';

export type ValidityTypes = keyof Omit<ValidityState, 'valid'>;

type TextInputProps = ExposeInputTextProps & {
  validationMessages?: Partial<Record<ValidityTypes, string>>;
  validatesOnBlur?: boolean;
  validatesOnChange?: boolean;
  customValidation?: (value: string) => boolean;
  controlledError?: string;
};

export const TextInput = ({
  label,
  validationMessages,
  customValidation,
  validatesOnChange = true,
  validatesOnBlur = true,
  controlledError,
  className,
  defaultValue,
  id,
  isRequired,
  name,
  onBlur,
  onChange,
  onFocus,
  onInvalid,
  size = 'm',
  value,
  ...inputProps
}: TextInputProps) => {
  const { isMobile } = useBreakpoint();
  const { isModal } = useContext(ModalContext);
  const [error, setError] = useState(controlledError ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    setError(controlledError ?? '');
  }, [controlledError]);

  const getCustomValidationMessage = useCallback(
    (value: string) => {
      if (!customValidation) return '';

      if (!customValidation(value)) {
        const errorMessage = validationMessages?.customError;
        if (!errorMessage) {
          datadogRum.addError(`No custom error message provided for ${name} field.`);
          return 'Invalid value';
        }

        return errorMessage;
      }

      return '';
    },
    [customValidation, validationMessages, name],
  );

  const syncCustomValidity = useCallback(
    (value: string) => {
      if (!inputRef.current) return '';

      if (controlledError) {
        inputRef.current.setCustomValidity(controlledError);
        return controlledError;
      }

      if (!customValidation) {
        inputRef.current.setCustomValidity('');
        return '';
      }

      const customErrorMessage = getCustomValidationMessage(value);
      inputRef.current.setCustomValidity(customErrorMessage);
      return customErrorMessage;
    },
    [controlledError, customValidation, getCustomValidationMessage],
  );

  const updateValidity = useCallback(() => {
    const input = inputRef.current;

    // If controlledError is set, use it and skip validation
    if (controlledError) {
      setError(controlledError);
      return;
    }
    // If controlledError is explicitly undefined (cleared by parent), clear error immediately
    // and continue with validation
    if (controlledError === undefined) {
      setError('');
    }

    if (!input) return;

    const customErrorMessage = syncCustomValidity(input.value);

    if (customErrorMessage) {
      setError(customErrorMessage);
      return;
    }

    const validity = input.validity;

    if (!validity || validity.valid) {
      setError('');
      return;
    }

    for (const key in validity) {
      if (validity[key as ValidityTypes]) {
        const errorMessage = validationMessages?.[key as ValidityTypes];
        if (!errorMessage) {
          console.warn('No error message provided for:', key);
          continue;
        }
        setError(errorMessage);
        break;
      }
    }
  }, [controlledError, syncCustomValidity, validationMessages]);

  useEffect(() => {
    syncCustomValidity(inputRef.current?.value ?? '');
  }, [syncCustomValidity, value]);

  const inputId = id ?? name;

  const syncCurrentCustomValidity = useCallback(() => {
    syncCustomValidity(inputRef.current?.value ?? '');
  }, [syncCustomValidity]);

  useInputValidation({
    inputRef,
    updateValidity,
    syncValidity: syncCurrentCustomValidity,
    validationMessages,
    validatesOnChange,
  });

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);

      // on mobile iOS, wait for the native keyboard to scroll the label out of view
      // and then scroll it back into view
      if (!isMobile || !isModal) return;
      setTimeout(() => {
        labelRef.current?.scrollIntoView({
          behavior: 'instant',
          block: 'start',
        });
      }, 100);
    },
    [isMobile, isModal, onFocus],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);

      if (validatesOnBlur) {
        updateValidity();
      }
    },
    [onBlur, updateValidity, validatesOnBlur],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      syncCustomValidity(event.currentTarget.value);

      if (validatesOnChange) {
        updateValidity();
      }
    },
    [onChange, syncCustomValidity, updateValidity, validatesOnChange],
  );

  const handleInvalid = useCallback(
    (event: InvalidEvent<HTMLInputElement>) => {
      onInvalid?.(event);
      updateValidity();
    },
    [onInvalid, updateValidity],
  );

  const normalizedInputProps = normalizeInputTextValueProps({
    ...inputProps,
    ...(value !== undefined ? { value } : {}),
    ...(defaultValue !== undefined ? { defaultValue } : {}),
    error: Boolean(error),
    helperText: error,
    id: inputId,
    isRequired,
    name,
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    onInvalid: handleInvalid,
    size,
    'data-form-error': error ? 'true' : 'false',
  });

  return (
    <div className={className}>
      {Boolean(label) && (
        <label className={styles.label} data-required={isRequired} htmlFor={inputId} ref={labelRef}>
          {label}
        </label>
      )}
      <UiCoreInputText {...normalizedInputProps} ref={inputRef} />
    </div>
  );
};
